import { useEffect, useState } from "react";
import axios from "axios";
import {
  BYOK_PROVIDERS,
  BYOK_LABELS,
  getByokProvider,
  setByokProvider,
  getByokKey,
  setByokKey,
  clearByokKey,
  hasAnyByokKey,
} from "../../lib/byokStore";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phase I.2 — PWA "AI Engine" settings panel.
 *
 * Mirrors the Android AiEngineSection: user picks a provider (Gemini /
 * OpenAI / Anthropic) and pastes their key. Save → key lives in
 * localStorage; PWA chat goes direct browser → provider from now on.
 * Also flips `has_byok_active=true` on the server so upgrade screens
 * show the 50%-off BYOK tier pricing.
 */
export function AiEngineSection({ onByokChanged }) {
  const [provider, setProviderState] = useState(getByokProvider());
  const [keyDraft, setKeyDraft] = useState(getByokKey());
  const [keyVisible, setKeyVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  // Reload the key when provider changes
  useEffect(() => {
    setKeyDraft(getByokKey(provider));
  }, [provider]);

  const handleProviderChange = (p) => {
    setProviderState(p);
    setByokProvider(p);
  };

  const syncToServer = async (active) => {
    try {
      await axios.post(`${API}/me/byok-status`, { has_byok_active: active });
      if (typeof onByokChanged === "function") onByokChanged(active);
    } catch (_) {
      // Non-fatal — server sync retries on next /api/me round-trip
    }
  };

  const handleSave = async () => {
    if (!keyDraft.trim()) return;
    setBusy(true);
    setByokKey(provider, keyDraft);
    await syncToServer(true);
    setStatus("saved");
    setBusy(false);
    setTimeout(() => setStatus(null), 2200);
  };

  const handleClear = async () => {
    setBusy(true);
    clearByokKey(provider);
    setKeyDraft("");
    // Only flip server flag off if NO key remains across providers
    if (!hasAnyByokKey()) {
      await syncToServer(false);
    }
    setStatus("cleared");
    setBusy(false);
    setTimeout(() => setStatus(null), 2200);
  };

  const savedKey = getByokKey(provider);
  const isDirty = keyDraft.trim() !== "" && keyDraft !== savedKey;

  return (
    <div className="ai-engine-section" data-testid="ai-engine-section">
      <h3 className="settings-section-title">
        <span className="settings-section-icon">✦</span> AI Engine
      </h3>
      <p className="settings-section-subtitle">
        Bring your own API key. Aura uses it directly from your browser — our servers stay out of it,
        and you get <strong>50% off</strong> any tier.
      </p>

      <div className="byok-provider-tabs" data-testid="byok-provider-tabs">
        {BYOK_PROVIDERS.map((p) => (
          <button
            key={p}
            className={`byok-provider-tab ${provider === p ? "byok-provider-tab-active" : ""}`}
            onClick={() => handleProviderChange(p)}
            data-testid={`byok-provider-${p}`}
          >
            {BYOK_LABELS[p]}
          </button>
        ))}
      </div>

      <label className="byok-key-label">
        Your {BYOK_LABELS[provider]} API key
        <div className="byok-key-input-row">
          <input
            type={keyVisible ? "text" : "password"}
            value={keyDraft}
            onChange={(e) => setKeyDraft(e.target.value)}
            placeholder={provider === "gemini" ? "AIzaSy…" : provider === "openai" ? "sk-…" : "sk-ant-…"}
            className="byok-key-input"
            data-testid="byok-key-input"
            spellCheck={false}
            autoComplete="off"
          />
          <button
            className="byok-key-visibility-toggle"
            onClick={() => setKeyVisible((v) => !v)}
            type="button"
            data-testid="byok-key-visibility-toggle"
            aria-label={keyVisible ? "Hide key" : "Show key"}
          >
            {keyVisible ? "Hide" : "Show"}
          </button>
        </div>
      </label>

      <div className="byok-actions">
        <button
          onClick={handleSave}
          disabled={!isDirty || busy}
          className="byok-save-btn"
          data-testid="byok-save-btn"
        >
          {busy && status !== "cleared" ? "Saving…" : savedKey ? "Update key" : "Save key"}
        </button>
        {savedKey && (
          <button
            onClick={handleClear}
            disabled={busy}
            className="byok-clear-btn"
            data-testid="byok-clear-btn"
          >
            Remove key
          </button>
        )}
      </div>

      {status === "saved" && (
        <p className="byok-status-msg byok-status-saved" data-testid="byok-status-saved">
          ✓ Key saved. Aura will use it directly from now on, and your discount is unlocked.
        </p>
      )}
      {status === "cleared" && (
        <p className="byok-status-msg byok-status-cleared" data-testid="byok-status-cleared">
          Key removed. Aura is back to her default brain.
        </p>
      )}

      <div className="byok-help-links">
        {provider === "gemini" && (
          <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer">
            Get a Gemini key →
          </a>
        )}
        {provider === "openai" && (
          <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">
            Get an OpenAI key →
          </a>
        )}
        {provider === "anthropic" && (
          <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer">
            Get a Claude key →
          </a>
        )}
      </div>
    </div>
  );
}
